import React, { useState, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import styled from 'styled-components/macro';

import GroupIcon from '@material-ui/icons/Group';
import FindInPageIcon from '@material-ui/icons/FindInPage';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import { Tables } from '@deeptrue-frontend/ui';

import { OrganizationsState } from 'store/modules/organizations/organizations';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { useFormatMessage } from 'components/IntlProvider';
import Forms from 'components/Forms';
import Tokens from '../../assets/icons/tokens.svg';

const { Link, ROUTES, withRouter } = RouterProvider;
const {
  TableRow,
  TableCell,
  TableBody,
  TableHeader,
  TablePagination,
  TableFooter,
} = Tables;

const Table = styled(Tables.Table)`
  .icons-wrapper a {
    padding: 0 4px;
  }
  thead tr th:first-child,
  tbody tr td:first-child {
    max-width: 14rem;
  }
  td:nth-child(7),
  tr:nth-child(7) {
    min-width: 16rem;
  }
`;

interface OrganizationsProps
  extends Pick<OrganizationsState, 'count' | 'list'> {
  currentPage: number;
}

const Organizations = ({
  count,
  list,
  history,
  currentPage,
}: OrganizationsProps & RouteComponentProps) => {
  const t = useFormatMessage();

  const [inviteUserModal, setInviteUserModal] = useState<{
    isOpen: boolean;
    orgName: null | string;
  }>({
    isOpen: false,
    orgName: null,
  });
  const onChangePage = (page: number) => {
    const params = { limit: 20, offset: 20 * page - 20 };
    history.push(
      `${ROUTES.ADMIN.ORGANIZATIONS.LIST}?${queryString.stringify(params)}`
    );
  };

  const headerCells = useMemo(
    () => [
      t('dashboard.organizations.name', 'Name'),
      'ID',
      t('dashboard.organizations.all', 'All Projects'),
      t('dashboard.organizations.draft', 'Draft Projects'),
      t('dashboard.organizations.credits_available', 'Credits Available'),
      t('dashboard.organizations.credits_spent', 'Credits Spent'),
      {
        text: t('dashboard.organizations.resources', 'Resources'),
        props: { align: 'right' },
      },
    ],
    // eslint-disable-next-line
    []
  );

  return (
    <>
      <Table>
        <TableHeader cells={headerCells} />
        <TableBody>
          {list.map((organization) => (
            <TableRow key={organization.id}>
              <TableCell>{organization.name}</TableCell>
              <TableCell>{organization.id}</TableCell>
              <TableCell>{organization.stats.projects}</TableCell>
              <TableCell>{organization.stats.drafts}</TableCell>
              <TableCell>{organization.available_tokens}</TableCell>
              <TableCell>{organization.total_spent_tokens}</TableCell>
              <TableCell align="right" className="icons-wrapper">
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${organization.id}/credits`}
                >
                  <Tooltip
                    title={t('tooltip.organizations.credits', 'Credits')}
                    placement="top"
                  >
                    <img src={Tokens} alt="token" style={{ width: '24px' }} />
                  </Tooltip>
                </Link>
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${organization.id}`}
                >
                  <Tooltip
                    title={t('tooltip.organizations.edit', 'Edit info')}
                    placement="top"
                  >
                    <EditIcon />
                  </Tooltip>
                </Link>
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${organization.codename}/projects`}
                >
                  <Tooltip
                    title={t('tooltip.organizations.projects', 'Projects')}
                    placement="top"
                  >
                    <FindInPageIcon />
                  </Tooltip>
                </Link>
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${organization.codename}/users`}
                >
                  <Tooltip
                    title={t('tooltip.organizations.users', 'Users')}
                    placement="top"
                  >
                    <GroupIcon />
                  </Tooltip>
                </Link>
                <Link
                  onClick={(e: any) => {
                    e.preventDefault();

                    return setInviteUserModal({
                      isOpen: true,
                      orgName: organization.codename,
                    });
                  }}
                  to=""
                >
                  <Tooltip
                    title={t('tooltip.organizations.invite', 'Invite user')}
                    placement="top"
                  >
                    <PersonAddIcon />
                  </Tooltip>
                </Link>
                <Link
                  to={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${organization.codename}/users/invitations`}
                >
                  <Tooltip
                    title={t(
                      'tooltip.organizations.invitations',
                      'Invitations'
                    )}
                    placement="top"
                  >
                    <MailOutlineIcon />
                  </Tooltip>
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TablePagination
            count={count}
            currentPage={currentPage}
            rowsPerPage={20}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
      <Forms.AddUserModal
        isVisible={inviteUserModal.isOpen}
        orgToInvite={inviteUserModal.orgName}
        onClose={() =>
          setInviteUserModal({
            isOpen: false,
            orgName: null,
          })
        }
      />
    </>
  );
};

export default withRouter(Organizations);
